/**
 * Forced-update sync-queue backup seam (phase C.6 → C.7).
 *
 * Before a *forced* update reloads onto a new build, any operations still sitting
 * in the offline sync queue must be offered as a downloadable backup (legal
 * safety net — a sync-schema bump could otherwise strand legally-critical ops).
 * C.7 implements the real exporter (`exportSyncQueue` + download). C.6 only wires
 * the call site: the force-update handler invokes `triggerForcedUpdateBackup`
 * synchronously, inside the click gesture, *before* any `await`.
 *
 * CRITICAL — the registered handler MUST run synchronously and trigger the
 * browser download within the user gesture. Browsers block downloads that start
 * after an `await`/`setTimeout`/`.then`. C.7's handler is responsible for keeping
 * the export data ready so the actual `triggerDownload` is synchronous.
 *
 * Until C.7 registers a handler this is a no-op, so the update flow works today
 * without a backup step.
 */
export type ForcedUpdateBackupHandler = () => void;

let handler: ForcedUpdateBackupHandler | null = null;

/** Registers C.7's synchronous backup-download handler. Pass `null` to detach (tests). */
export const setForcedUpdateBackupHandler = (next: ForcedUpdateBackupHandler | null): void => {
  handler = next;
};

/**
 * Synchronously triggers a sync-queue backup download. Must be called inside a
 * user gesture, before any `await`. No-op until C.7 wires the exporter.
 */
export const triggerForcedUpdateBackup = (): void => {
  handler?.();
};
